import { useMemo } from 'react';

import { Commitment, CommitmentAssignment } from '@cast/types';

import { useAssignmentsQuery } from 'hooks/queries/commitments';

import { useAllAssignmentsFetchingDisabled } from '../useAllAssignmentsFetchingDisabled';
import {
  normalizeAzureRiCommitments,
  normalizeAzureSpCommitments,
  normalizeAwsSpCommitments,
  normalizeAwsRiCommitments,
  normalizeGcpResourceCudCommitments,
  normalizeGcpFlexCudCommitments,
  normalizeAwsOdcrCommitments,
  normalizeAwsCapacityBlockCommitments,
  normalizeGcpReservationCommitments,
} from './_utils/normalizeCommitments';
import { GroupedCommitments } from '../../_types';
import { NormalizedCommitment, CommitmentCloudService } from '../../types';
import { useGroupedCommitments } from '../useGroupedCommitments';

const defaultGroupedCommitments = {
  AZURE_RI: [],
  AZURE_SP: [],
  GCP_RESOURCE_CUD: [],
  AWS_RI: [],
  AWS_SP: [],
  GCP_FLEX_CUD: [],
  AWS_ODCR: [],
  AWS_CAPACITY_BLOCK: [],
  GCP_RESERVATION: [],
} satisfies GroupedCommitments;

const defaultAssignments = [] satisfies CommitmentAssignment[];

const normalizeFnMap: Record<
  CommitmentCloudService,
  (
    commitments: Commitment[],
    assignments: CommitmentAssignment[]
  ) => NormalizedCommitment[]
> = {
  AZURE_RI: normalizeAzureRiCommitments,
  AZURE_SP: normalizeAzureSpCommitments,
  GCP_RESOURCE_CUD: normalizeGcpResourceCudCommitments,
  AWS_RI: normalizeAwsRiCommitments,
  AWS_SP: normalizeAwsSpCommitments,
  GCP_FLEX_CUD: normalizeGcpFlexCudCommitments,
  AWS_ODCR: normalizeAwsOdcrCommitments,
  AWS_CAPACITY_BLOCK: normalizeAwsCapacityBlockCommitments,
  GCP_RESERVATION: normalizeGcpReservationCommitments,
};

export type NormalizedCommitments = Record<
  CommitmentCloudService,
  NormalizedCommitment[]
>;

export const useNormalizedCommitments = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) => {
  const assignmentsFetchingDisabled = useAllAssignmentsFetchingDisabled();

  const {
    data: groupedCommitments = defaultGroupedCommitments,
    isLoading: commitmentsLoading,
    isError: isCommitmentsError,
    error: commitmentsError,
    refetch: refetchCommitments,
  } = useGroupedCommitments({ enabled });
  const {
    data: assignments = defaultAssignments,
    isLoading: assignmentsLoading,
    isError: isAssignmentsError,
    error: assignmentsError,
    refetch: refetchAssignments,
  } = useAssignmentsQuery({
    enabled: enabled && !assignmentsFetchingDisabled,
  });

  const normalizedData = useMemo(() => {
    const result: NormalizedCommitments = { ...defaultGroupedCommitments };

    Object.entries(groupedCommitments).forEach(
      ([cloudService, commitments]) => {
        const service = cloudService as CommitmentCloudService;
        result[service] = normalizeFnMap[service](commitments, assignments);
      }
    );
    return result;
  }, [groupedCommitments, assignments]);

  return {
    normalizedData,
    isLoading: commitmentsLoading || assignmentsLoading,
    isError: isCommitmentsError || isAssignmentsError,
    refetch: () => {
      refetchCommitments();
      if (!assignmentsFetchingDisabled) {
        refetchAssignments();
      }
    },
    error: commitmentsError || assignmentsError,
  };
};
